/**
 * BillingCancel - Stripe checkout cancel page
 * 
 * Displayed when user cancels checkout. Offers to retry or return appropriately
 * based on user type (consultant vs client).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Stack,
} from '@mui/material';
import { Cancel as CancelIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useCompany } from '../contexts/CompanyContext';

const BillingCancel: React.FC = () => {
  const navigate = useNavigate();
  const { isConsultantUser, selectedCompany } = useCompany();
  const [countdown, setCountdown] = useState(10);

  // Determine redirect URL based on user type
  const getRedirectUrl = () => {
    if (isConsultantUser) {
      // Consultants go to admin billing settings
      return '/admin?tab=settings';
    } else if (selectedCompany) {
      // Clients go to their company settings with billing tab
      return `/company/${selectedCompany.id}/settings?tab=billing`;
    } else {
      // Fallback to dashboard
      return '/';
    }
  };

  const redirectUrl = getRedirectUrl();

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(redirectUrl);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, redirectUrl]);

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="80vh"
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
          }}
        >
          <CancelIcon
            sx={{
              fontSize: 80,
              color: 'warning.main',
              mb: 2,
            }}
          />
          
          <Typography variant="h4" gutterBottom fontWeight="bold" color="warning.dark">
            Payment Cancelled
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            No worries! Your payment was cancelled and you haven't been charged.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Redirecting in {countdown} seconds...
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => navigate(redirectUrl)}
            >
              Back to Settings
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={() => navigate(redirectUrl)}
            >
              Try Again
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
};

export default BillingCancel;

